import { faClose, faPlug } from "@fortawesome/free-solid-svg-icons"
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import GetSpace from "../../../utils/space"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { SIZE } from "../../../configuration/Sizes"
import { useMetaSave } from "../../../hooks/Meta/useMetaSave"
import type { UpdateMetaNotificationPreferencesPayload } from "../../../hooks/Meta/useMetaSave"
import { useError } from "../../../context/useErrorContext"
import { useCreateMetaIntegration } from "../../../hooks/Meta/useCreateMetaIntegration"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { MetaPhoneRegistrationPopup } from "./MetaPhoneRegistrationPopup"
import { MetaTestPopup, type MetaTestSendPayload } from "./MetaTestPopup"

interface MetaIntegrationPopupProps {
  onClose: () => void
}

export const MetaIntegrationPopup: React.FC<MetaIntegrationPopupProps> = ({ onClose }) => {
  const { t } = useTranslation("company")

  const { setError } = useError()

  const {
    accountSid,
    authToken,
    whatsappFromNumber,
    isEnabled,
    metaSettings,
    isStatusChangedNotificationEnabled,
    isFifteenDaysCampaignEnabled,
    isPrizeNotificationEnabled,
    statusChangedTemplate,
    fifteenDaysCampaignTemplate,
    prizeTemplate,
    fifteenDaysIntervalDays,
    handleAccountSidChange,
    handleAuthTokenChange,
    handleWhatsappFromNumberChange,
    handleEnabledChange,
    handleStatusChange,
    handleSubmitForm,
    handleInitialValue,
    setIsStatusChangedNotificationEnabled,
    setIsFifteenDaysCampaignEnabled,
    setIsPrizeNotificationEnabled,
    setStatusChangedTemplate,
    setFifteenDaysCampaignTemplate,
    setPrizeTemplate,
    setFifteenDaysIntervalDays,
  } = useCreateMetaIntegration()

  const {
    handleGetMetaIntegration,
    handleSave,
    handleTestConnection,
    handleToggle,
    handleUpdateNotificationPreferences,
    handleSendWhatsappTest,
    handleRegisterPhoneNumber,
    handleCheckPhoneNumberStatus,
    handleGetTemplateStatuses,
    handleSyncTemplate,
  } = useMetaSave()

  const [testNotificationType, setTestNotificationType] = useState<string>("statusChanged")

  const [isTestPopupOpen, setIsTestPopupOpen] = useState<boolean>(false)

  const [isChangingToken, setIsChangingToken] = useState<boolean>(false)

  const [registerPin, setRegisterPin] = useState<string>("")

  const [phoneRegisterStatus, setPhoneRegisterStatus] = useState<string>("")

  const [templateStatuses, setTemplateStatuses] = useState<
    Record<string, { status: string; reason?: string; isSendAllowed: boolean }>
  >({})

  const [isTemplateStatusesLoading, setIsTemplateStatusesLoading] = useState<boolean>(true)

  const [isPhoneRegistrationPopupOpen, setIsPhoneRegistrationPopupOpen] = useState<boolean>(false)

  const formScrollRef = useRef<HTMLFormElement | null>(null)

  const canLoadTemplateStatuses = (settings?: { isValidated?: boolean } | null) => settings?.isValidated === true

  const loadTemplateStatuses = async (settingsToValidate?: { isValidated?: boolean } | null) => {
    const referenceSettings = settingsToValidate ?? metaSettings

    if (!canLoadTemplateStatuses(referenceSettings)) {
      setTemplateStatuses({})
      setIsTemplateStatusesLoading(false)
      return
    }

    setIsTemplateStatusesLoading(true)

    await handleGetTemplateStatuses((data) => setTemplateStatuses(data ?? {}))

    setIsTemplateStatusesLoading(false)
  }

  useEffect(() => {
    const loadInitialData = async () => {
      await handleGetMetaIntegration((settings) => {
        handleInitialValue(settings)

        if (!canLoadTemplateStatuses(settings)) {
          setTemplateStatuses({})

          setIsTemplateStatusesLoading(false)
          return
        }

        void loadTemplateStatuses(settings)
      })
    }

    void loadInitialData()
  }, [])

  const refreshIntegrationState = async () => {
    await handleGetMetaIntegration((settings) => {
      handleInitialValue(settings)

      if (!canLoadTemplateStatuses(settings)) {
        setTemplateStatuses({})

        setIsTemplateStatusesLoading(false)
        return
      }

      void loadTemplateStatuses(settings)
    })
  }

  const onSave = async () => {
    const formData = handleSubmitForm()

    if ("errors" in formData) {
      const firstError = formData.errors[0]

      if (firstError) {
        setError(t("general:label_title_erro"), firstError.message)
      }

      return
    }

    await handleSave(formData, async () => {
      await handleTestConnection(
        {
          accountSid: accountSid.trim(),
          authToken: authToken.trim(),
        },
        () => {
          handleStatusChange(t("label_meta_status_connected"))
          void refreshIntegrationState()
        },
        (message) => {
          handleStatusChange(message)
          void refreshIntegrationState()
        },
      )
    })
  }

  const handleIntegrationToggle = async (nextValue: boolean) => {
    if (nextValue === isEnabled) {
      return
    }

    await handleToggle(nextValue, () => {
      handleEnabledChange(nextValue)
      void refreshIntegrationState()
    })
  }

  const notificationTemplateOptions = useMemo(
    () => [
      {
        value: "statusChanged",
        label: t("label_meta_notify_status_changed"),
        template: statusChangedTemplate.trim() || t("label_meta_notify_status_changed_placeholder"),
      },
      {
        value: "fifteenDaysCampaign",
        label: t("label_meta_campaign_fifteen_days"),
        template: fifteenDaysCampaignTemplate.trim() || t("label_meta_campaign_fifteen_days_placeholder"),
      },
      {
        value: "prizeCampaign",
        label: t("label_meta_campaign_prize"),
        template: prizeTemplate.trim() || t("label_meta_campaign_prize_placeholder"),
      },
    ],
    [fifteenDaysCampaignTemplate, prizeTemplate, statusChangedTemplate, t],
  )

  const notificationTypeToTemplateKeyMap: Record<string, string> = {
    statusChanged: "status_changed",
    fifteenDaysCampaign: "fifteen_days_campaign",
    prizeCampaign: "prize_campaign",
  }

  const getTemplateStatus = (templateKey: string) => templateStatuses[templateKey]?.status ?? "NOT_CREATED"

  const getTemplateReason = (templateKey: string) => templateStatuses[templateKey]?.reason ?? ""

  const approvedTemplateOptions = useMemo(
    () =>
      notificationTemplateOptions.filter((option) => {
        const key = notificationTypeToTemplateKeyMap[option.value]
        return key && getTemplateStatus(key).toUpperCase() === "APPROVED"
      }),
    [notificationTemplateOptions, templateStatuses],
  )

  const selectedTestNotificationType = useMemo(() => {
    const selectedTemplateIsApproved = approvedTemplateOptions.some((option) => option.value === testNotificationType)

    if (selectedTemplateIsApproved) {
      return testNotificationType
    }

    return approvedTemplateOptions[0]?.value ?? testNotificationType
  }, [approvedTemplateOptions, testNotificationType])

  const isTemplateEditLocked = (templateKey: string) => {
    const status = getTemplateStatus(templateKey).toUpperCase()
    return status === "PENDING" || status === "IN_REVIEW"
  }

  const formattedLastConnectionTest = useMemo(() => {
    if (!metaSettings?.lastConnectionTestAt) {
      return t("label_meta_auth_never_tested")
    }

    return new Date(metaSettings.lastConnectionTestAt).toLocaleString()
  }, [metaSettings?.lastConnectionTestAt, t])

  const hasSavedToken = useMemo(
    () => (metaSettings?.authTokenEncrypted?.trim().length ?? 0) > 0,
    [metaSettings?.authTokenEncrypted],
  )

  const authStatusPresentation = useMemo(() => {
    if (!metaSettings) {
      return {
        label: t("label_meta_auth_status_needs_auth"),
        className: "bg-amber-100 text-amber-800 border-amber-200",
      }
    }

    if (metaSettings.isValidated) {
      return {
        label: t("label_meta_auth_status_valid"),
        className: "bg-emerald-100 text-emerald-800 border-emerald-200",
      }
    }

    if (hasSavedToken) {
      return {
        label: t("label_meta_auth_status_needs_reauth"),
        className: "bg-red-100 text-red-800 border-red-200",
      }
    }

    return {
      label: t("label_meta_auth_status_needs_auth"),
      className: "bg-amber-100 text-amber-800 border-amber-200",
    }
  }, [hasSavedToken, metaSettings, t])

  const canManageTemplates = metaSettings?.isValidated === true

  const hasMetaCredentialFields =
    accountSid.trim().length > 0 &&
    whatsappFromNumber.trim().length > 0 &&
    (authToken.trim().length > 0 || hasSavedToken)

  const getTemplateCardClass = (templateKey: string) => {
    const status = getTemplateStatus(templateKey).toUpperCase()

    if (status === "APPROVED") {
      return "border-emerald-400 bg-emerald-50"
    }

    if (status === "PENDING" || status === "IN_REVIEW") {
      return "border-amber-400 bg-amber-50"
    }

    if (status === "REJECTED") {
      return "border-rose-400 bg-rose-50"
    }

    return "border-slate-300 bg-slate-50"
  }

  const onAutoSaveNotificationSettings = (overrides: Partial<UpdateMetaNotificationPreferencesPayload> = {}) => {
    const currentScrollTop = formScrollRef.current?.scrollTop ?? 0

    const payload: UpdateMetaNotificationPreferencesPayload = {
      isStatusChangedNotificationEnabled: isStatusChangedNotificationEnabled,
      isFifteenDaysCampaignEnabled: isFifteenDaysCampaignEnabled,
      isPrizeNotificationEnabled: isPrizeNotificationEnabled,
      ...overrides,
    }

    void handleUpdateNotificationPreferences(payload, () => {
      if (formScrollRef.current) {
        formScrollRef.current.scrollTop = currentScrollTop
      }
    })
  }

  useEffect(() => {
    if (!hasSavedToken) {
      setIsChangingToken(true)
      return
    }

    if (authToken.trim().length === 0) {
      setIsChangingToken(false)
    }
  }, [authToken, hasSavedToken])

  const onSendTestMessage = async (payload: MetaTestSendPayload) => {
    if (payload.toPhone.trim().length === 0) {
      setError(t("general:label_title_erro"), t("label_meta_test_phone_required"))
      return
    }

    const selectedTemplateKeyForTest = notificationTypeToTemplateKeyMap[payload.notificationType]

    const selectedStatus = selectedTemplateKeyForTest
      ? getTemplateStatus(selectedTemplateKeyForTest).toUpperCase()
      : "NOT_CREATED"

    if (selectedStatus !== "APPROVED") {
      setError(
        t("general:label_title_erro"),
        t("label_meta_test_requires_template_approved", { status: selectedStatus }),
      )
      return
    }

    await handleSendWhatsappTest(
      {
        toPhone: payload.toPhone.trim(),
        messageBody: payload.messageBody,
        templateKey: selectedTemplateKeyForTest,
        tokenValues: payload.tokenValues,
      },
      () => {
        void refreshIntegrationState()
      },
    )
  }

  const parsePhoneStatusPayload = (payload: any) => {
    const translateMetaStatus = (value?: string) => {
      const normalized = (value ?? "").toUpperCase()

      const map: Record<string, string> = {
        VERIFIED: t("label_meta_status_verified"),
        UNVERIFIED: t("label_meta_status_unverified"),
        PENDING: t("label_meta_status_pending"),
        APPROVED: t("label_meta_status_approved"),
        REJECTED: t("label_meta_status_rejected"),
      }

      return map[normalized] ?? (value || "-")
    }

    if (!payload) {
      return t("label_meta_phone_status_unknown")
    }

    if (typeof payload === "string") {
      try {
        const parsed = JSON.parse(payload)
        return `${t("label_meta_phone_status_code_verification")}: ${translateMetaStatus(parsed.code_verification_status)} | ${t("label_meta_phone_status_name")}: ${translateMetaStatus(parsed.name_status)}`
      } catch {
        return payload
      }
    }

    return `${t("label_meta_phone_status_code_verification")}: ${translateMetaStatus(payload.code_verification_status)} | ${t("label_meta_phone_status_name")}: ${translateMetaStatus(payload.name_status)}`
  }

  const onRegisterPhoneNumber = async () => {
    if (whatsappFromNumber.trim().length === 0) {
      setError(t("general:label_title_erro"), t("error_meta_whatsapp_from_required"))
      return
    }

    const sanitizedPin = registerPin.replace(/\D/g, "").trim()
    if (sanitizedPin.length > 0 && sanitizedPin.length !== 6) {
      setError(t("general:label_title_erro"), t("error_meta_phone_registration_pin_length"))
      return
    }

    await handleRegisterPhoneNumber(
      {
        phoneNumberId: whatsappFromNumber.trim(),
        authToken: authToken.trim(),
        pin: sanitizedPin,
      },
      () => {
        void onCheckPhoneNumberStatus()
      },
    )
  }

  const onCheckPhoneNumberStatus = async () => {
    if (whatsappFromNumber.trim().length === 0) {
      setError(t("general:label_title_erro"), t("error_meta_whatsapp_from_required"))
      return
    }

    await handleCheckPhoneNumberStatus(
      {
        phoneNumberId: whatsappFromNumber.trim(),
        authToken: authToken.trim(),
      },
      (data) => {
        setPhoneRegisterStatus(parsePhoneStatusPayload(data))
      },
    )
  }

  const onSyncTemplateByKey = async (templateKey: string, templateBody: string) => {
    await handleSyncTemplate(
      {
        templateKey,
        templateBody,
        authToken: authToken.trim(),
      },
      () => {
        void refreshIntegrationState()
      },
    )
  }

  const renderTemplateActionButton = (templateKey: string, templateBody: string) => {
    const status = getTemplateStatus(templateKey).toUpperCase()

    if (status === "PENDING" || status === "IN_REVIEW") {
      return (
        <span className="inline-flex items-center rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
          {t("label_meta_template_waiting_approval")}
        </span>
      )
    }

    const label = status === "APPROVED" ? t("label_meta_template_update_button") : t("label_meta_template_sync_button")

    return (
      <button
        type="button"
        className="inline-flex items-center rounded border border-blue-300 bg-blue-100 px-3 py-1 text-[11px] font-semibold text-blue-800 hover:bg-blue-200"
        onClick={() => void onSyncTemplateByKey(templateKey, templateBody)}
        data-interaction-description-key="interactionDescriptions:pages.myRestaurant.myCompany.metaIntegrationPopup.button.voidOnSyncTemplateByKeyTemplateKeyTemplateBody"
      >
        {label}
      </button>
    )
  }

  const renderToggleWithHelp = ({
    title,
    value,
    onChange,
    helpKey,
    disabled,
  }: {
    title: string
    value: boolean
    onChange: (value: boolean) => void
    helpKey: string
    disabled?: boolean
  }) => (
    <div className="flex items-center gap-2">
      <div className="flex-1">
        <SimpleToggle
          title={title}
          value={value}
          onChange={onChange}
          disabled={disabled}
          animationDuration="0.35s"
          descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaIntegrationPopup.toggle.title"
        />
      </div>

      <PopupHelpButton
        helpKey="pages.myrestaurant.mycompany.metaintegrationpopup.1"
        variant="global"
        title={t(`helpPopup:global.${helpKey}.title`)}
        description={t(`helpPopup:global.${helpKey}.description`)}
        bullets={t(`helpPopup:global.${helpKey}.bullets`, { returnObjects: true }) as string[]}
      />
    </div>
  )

  const renderTemplateStatusInfo = (templateKey: string) => {
    const status = getTemplateStatus(templateKey).toUpperCase()
    const colorClass =
      status === "APPROVED"
        ? "text-emerald-700"
        : status === "PENDING" || status === "IN_REVIEW"
          ? "text-amber-700"
          : status === "REJECTED"
            ? "text-rose-700"
            : "text-slate-600"

    return (
      <div className={`mt-1 text-[11px] ${colorClass}`}>
        <p>
          <strong>{t("label_meta_phone_status_title")}:</strong> {getTemplateStatus(templateKey)}
        </p>

        {getTemplateReason(templateKey) && (
          <p className="text-rose-700 mt-1">
            <strong>{t("label_meta_template_reason_title")}:</strong> {getTemplateReason(templateKey)}
          </p>
        )}
      </div>
    )
  }

  const isTemplateReadyForNotification = (templateKey: string) =>
    getTemplateStatus(templateKey).toUpperCase() === "APPROVED"

  const renderNotificationTemplateCard = ({
    title,
    value,
    onChange,
    helpKey,
    templateKey,
    templateValue,
    onTemplateChange,
    placeholder,
    extraContent,
  }: {
    title: string
    value: boolean
    onChange: (value: boolean) => void
    helpKey: string
    templateKey: string
    templateValue: string
    onTemplateChange: (value: string) => void
    placeholder: string
    extraContent?: ReactNode
  }) => (
    <section
      className={`rounded-lg border-2 p-2 ${value ? getTemplateCardClass(templateKey) : "border-slate-200 bg-white"}`}
    >
      {renderToggleWithHelp({
        title,
        value,
        onChange,
        helpKey,
        disabled: isTemplateEditLocked(templateKey),
      })}
      {value && (
        <>
          <GetSpace top={6} />

          {!isTemplateReadyForNotification(templateKey) && (
            <section className="mb-2 rounded border border-rose-300 bg-rose-50 p-2 text-[11px] text-rose-900">
              <p className="font-semibold">{t("label_meta_template_not_ready_title")}</p>
              <p className="mt-1">{t("label_meta_template_not_ready_desc")}</p>
            </section>
          )}

          {extraContent}

          <textarea
            className="w-full border-2 rounded-lg p-2 text-xs bg-white"
            rows={2}
            value={templateValue}
            onChange={(e) => onTemplateChange(e.target.value)}
            placeholder={placeholder}
            disabled={isTemplateEditLocked(templateKey)}
            data-interaction-description-key="interactionDescriptions:pages.myRestaurant.myCompany.metaIntegrationPopup.input.placeholder"
          />

          {isTemplateStatusesLoading ? (
            <section className="mt-1 rounded border border-amber-300 bg-amber-100 p-2">
              <p
                className="text-[11px] font-semibold text-amber-900 animate-pulse"
                style={{ animationDuration: "0.55s" }}
              >
                {t("label_meta_template_status_loading")}
              </p>
            </section>
          ) : (
            <>
              {renderTemplateStatusInfo(templateKey)}
              <div className="mt-1">{renderTemplateActionButton(templateKey, templateValue)}</div>
            </>
          )}
        </>
      )}
    </section>
  )

  const availableTemplateVariables = [
    { token: "{{customerName}}", description: t("label_meta_var_customer_name") },
    { token: "{{orderId}}", description: t("label_meta_var_order_id") },
    { token: "{{restaurantName}}", description: t("label_meta_var_restaurant_name") },
    { token: "{{orderStatus}}", description: t("label_meta_var_order_status") },
    { token: "{{orderDate}}", description: t("label_meta_var_order_date") },
    { token: "{{orderTotal}}", description: t("label_meta_var_order_total") },
    { token: "{{deliveryAddress}}", description: t("label_meta_var_delivery_address") },
    { token: "{{orderType}}", description: t("label_meta_var_order_type") },
    { token: "{{paymentStatus}}", description: t("label_meta_var_payment_status") },
    { token: "{{prizeName}}", description: t("label_meta_var_prize_name") },
  ]

  return (
    <>
      <ModalPortal>
        <div className={defaultStyle.overlay}>
          <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
            <div className={defaultStyle.alert}>
              <TitlePageResponsiveIconSmall
                title={t("label_title_meta_integration")}
                iconColor={THEME.colors.iconBGColor}
                icon={faPlug}
              >
                <div className={`${defaultStyle.ButtonsActions} flex items-center gap-1`}>
                  <PopupHelpButton
                    helpKey="pages.myrestaurant.mycompany.metaintegrationpopup.2"
                    variant="global"
                    title={t("helpPopup:global.metaIntegration.title")}
                    description={t("helpPopup:global.metaIntegration.description")}
                    bullets={t("helpPopup:global.metaIntegration.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaIntegrationPopup.button.onClose"
                  />
                </div>
              </TitlePageResponsiveIconSmall>

              <form
                ref={formScrollRef}
                className="p-2 max-h-[70vh] overflow-y-auto"
                onSubmit={(e) => {
                  e.preventDefault()
                  void onSave()
                }}
              >
                <section className="mb-3 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                  <p className="font-semibold">{t("label_meta_cost_warning_title")}</p>

                  <p className="mt-1">{t("label_meta_cost_warning_description")}</p>
                </section>

                <section className="rounded border border-slate-300 bg-slate-50 p-2">
                  <p className="text-xs font-semibold text-slate-700">{t("label_title_meta_integration")}</p>

                  <GetSpace top={8} />

                  <InputField
                    type="text"
                    placeholder={t("label_meta_account_sid")}
                    value={accountSid}
                    onChange={handleAccountSidChange}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaIntegrationPopup.input.metaAccountSid"
                  />

                  <GetSpace top={8} />

                  <InputField
                    type="text"
                    placeholder={t("label_meta_whatsapp_from")}
                    value={whatsappFromNumber}
                    onChange={handleWhatsappFromNumberChange}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaIntegrationPopup.input.metaWhatsappFrom"
                  />

                  <GetSpace top={8} />

                  {hasSavedToken && !isChangingToken ? (
                    <section className="rounded border border-emerald-200 bg-emerald-50 p-2">
                      <p className="text-xs text-emerald-700">{t("label_meta_saved_token_detected")}</p>
                      <button
                        type="button"
                        className="mt-2 text-xs font-semibold text-emerald-700 underline"
                        onClick={() => setIsChangingToken(true)}
                        data-interaction-description-key="interactionDescriptions:pages.myRestaurant.myCompany.metaIntegrationPopup.button.setIsChangingTokenTrue"
                      >
                        {t("label_meta_change_saved_token")}
                      </button>
                    </section>
                  ) : (
                    <>
                      <InputField
                        type="password"
                        placeholder={t("label_meta_auth_token")}
                        value={authToken}
                        onChange={handleAuthTokenChange}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaIntegrationPopup.input.metaAuthToken"
                      />
                      <p className="mt-1 text-[11px] text-slate-500">
                        {t("label_meta_auth_token_optional_for_test_hint")}
                      </p>
                    </>
                  )}

                  <GetSpace top={8} />

                  <section className="rounded border border-slate-200 bg-white p-2 text-xs text-slate-700">
                    <p>
                      <strong>{t("label_meta_auth_status_title")}:</strong>{" "}
                      <span
                        className={`inline-flex items-center rounded border px-2 py-0.5 text-[11px] font-semibold ${authStatusPresentation.className}`}
                      >
                        {authStatusPresentation.label}
                      </span>
                    </p>

                    <p className="mt-1">
                      <strong>{t("label_meta_auth_last_test_title")}:</strong> {formattedLastConnectionTest}
                    </p>

                    <p className="mt-1">
                      <strong>{t("label_meta_auth_validity_title")}:</strong> {t("label_meta_auth_validity_desc")}
                    </p>

                    {!metaSettings?.isValidated && metaSettings?.lastValidationErrorMessage && (
                      <p className="mt-1 text-red-600">
                        <strong>{t("label_meta_auth_last_error_title")}:</strong>{" "}
                        {metaSettings.lastValidationErrorMessage}
                      </p>
                    )}

                    <GetSpace top={6} />

                    {hasMetaCredentialFields && (
                      <section className="flex flex-wrap gap-2">
                        <Button
                          sizeNumber={SIZE.buttonCloseIconSize}
                          label={t("buttons:button_save")}
                          onClick={onSave}
                          type="primary"
                          size="small"
                          bgColor={THEME.colors.buttonBGSaveColor}
                          descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaIntegrationPopup.button.save"
                        />

                        <Button
                          sizeNumber={SIZE.buttonCloseIconSize}
                          label={t("label_meta_open_phone_registration_popup")}
                          onClick={() => setIsPhoneRegistrationPopupOpen(true)}
                          type="secondary"
                          size="small"
                          descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaIntegrationPopup.button.metaOpenPhoneRegistrationPopup"
                        />
                      </section>
                    )}

                    <GetSpace top={8} />

                    {renderToggleWithHelp({
                      title: isEnabled ? t("label_meta_disable") : t("label_meta_enable"),
                      value: isEnabled,
                      onChange: handleIntegrationToggle,
                      helpKey: "metaIntegrationToggle",
                    })}
                  </section>
                </section>

                <GetSpace top={10} />

                {canManageTemplates && isEnabled && (
                  <>
                    <div className={defaultStyle.divisionBar}></div>

                    {renderNotificationTemplateCard({
                      title: t("label_meta_notify_status_changed"),
                      value: isStatusChangedNotificationEnabled,
                      onChange: (value) => {
                        setIsStatusChangedNotificationEnabled(value)
                        onAutoSaveNotificationSettings({ isStatusChangedNotificationEnabled: value })
                      },
                      helpKey: "metaNotifyStatusChanged",
                      templateKey: "status_changed",
                      templateValue: statusChangedTemplate,
                      onTemplateChange: setStatusChangedTemplate,
                      placeholder: t("label_meta_notify_status_changed_placeholder"),
                    })}

                    <GetSpace top={6} />

                    {renderNotificationTemplateCard({
                      title: t("label_meta_campaign_fifteen_days"),
                      value: isFifteenDaysCampaignEnabled,
                      onChange: (value) => {
                        setIsFifteenDaysCampaignEnabled(value)
                        onAutoSaveNotificationSettings({ isFifteenDaysCampaignEnabled: value })
                      },
                      helpKey: "metaCampaignFifteenDays",
                      templateKey: "fifteen_days_campaign",
                      templateValue: fifteenDaysCampaignTemplate,
                      onTemplateChange: setFifteenDaysCampaignTemplate,
                      placeholder: t("label_meta_campaign_fifteen_days_placeholder"),
                      extraContent: (
                        <>
                          <InputField
                            type="number"
                            placeholder={t("label_meta_campaign_interval_placeholder")}
                            value={String(fifteenDaysIntervalDays)}
                            onChange={(e) => setFifteenDaysIntervalDays(Number(e.target.value || 15))}
                            descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaIntegrationPopup.input.metaCampaignIntervalPlaceholder"
                          />

                          <GetSpace top={6} />
                        </>
                      ),
                    })}

                    <GetSpace top={6} />

                    {renderNotificationTemplateCard({
                      title: t("label_meta_campaign_prize"),
                      value: isPrizeNotificationEnabled,
                      onChange: (value) => {
                        setIsPrizeNotificationEnabled(value)
                        onAutoSaveNotificationSettings({ isPrizeNotificationEnabled: value })
                      },
                      helpKey: "metaCampaignPrize",
                      templateKey: "prize_campaign",
                      templateValue: prizeTemplate,
                      onTemplateChange: setPrizeTemplate,
                      placeholder: t("label_meta_campaign_prize_placeholder"),
                    })}

                    <section className="mt-3 rounded-lg border-2 border-indigo-200 bg-indigo-50 p-3">
                      <p className="text-xs font-semibold text-indigo-900">{t("label_meta_templates_vars_title")}</p>

                      <p className="mt-1 text-[11px] text-indigo-800">{t("label_meta_templates_vars_hint")}</p>

                      <p className="mt-2 text-[11px] font-semibold text-indigo-900">
                        {t("label_meta_variable_how_to_use")}
                      </p>

                      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                        {availableTemplateVariables.map((item) => (
                          <div key={item.token} className="rounded border border-indigo-300 bg-white p-2">
                            <code className="text-[11px] font-bold text-indigo-800">{item.token}</code>
                            <p className="mt-1 text-[11px] text-slate-700">{item.description}</p>
                          </div>
                        ))}
                      </div>
                    </section>
                  </>
                )}

                <div className={defaultStyle.divisionBar}></div>

                <section className="flex justify-end gap-2">
                  {isEnabled && canManageTemplates && (
                    <Button
                      sizeNumber={SIZE.buttonCloseIconSize}
                      label={t("label_meta_open_test_popup")}
                      onClick={() => {
                        if (approvedTemplateOptions.length > 0) {
                          setTestNotificationType(selectedTestNotificationType)
                        }
                        setIsTestPopupOpen(true)
                      }}
                      type="secondary"
                      size="small"
                      descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaIntegrationPopup.button.metaOpenTestPopup"
                    />
                  )}
                </section>
              </form>
            </div>
          </Animate>
        </div>
      </ModalPortal>

      <MetaPhoneRegistrationPopup
        isOpen={isPhoneRegistrationPopupOpen && hasMetaCredentialFields}
        registerPin={registerPin}
        phoneRegisterStatus={phoneRegisterStatus}
        onClose={() => setIsPhoneRegistrationPopupOpen(false)}
        onRegisterPinChange={setRegisterPin}
        onRegisterPhoneNumber={onRegisterPhoneNumber}
        onCheckPhoneNumberStatus={onCheckPhoneNumberStatus}
      />

      <MetaTestPopup
        isOpen={isTestPopupOpen}
        approvedTemplateOptions={approvedTemplateOptions}
        initialNotificationType={selectedTestNotificationType}
        onClose={() => setIsTestPopupOpen(false)}
        onSendTestMessage={onSendTestMessage}
      />
    </>
  )
}
